import { useEffect, useState } from "react";

/**
 * Version visibility (round 45). Two jobs:
 *
 * 1. A non-descript build stamp in the page corner - the deployed commit,
 *    tiny and grey, so "which version am I looking at?" is answered by
 *    glancing, not guessing.
 * 2. A new-version watch: every few minutes (and whenever the tab regains
 *    focus) the app fetches /version.txt - emitted fresh with every deploy,
 *    never precached - and offers one-click refresh when the server has
 *    moved past the bundle this tab is running. Stale service-worker tabs
 *    stop silently lying.
 *
 * registerType is "prompt" (vite.config.ts), not "autoUpdate" - Workbox
 * parks a found update in the waiting state instead of silently activating
 * it on its own schedule, so refreshNow() below is the ONLY thing that ever
 * triggers an update, and it does so correctly: wait for the new worker to
 * actually finish installing, tell it to take over, and wait for that
 * handoff (controllerchange) before reloading. Reloading right after
 * calling update() - the previous bug - races the install: the first click
 * usually lands back on the same old bundle, so it silently needed a second
 * click once the install happened to finish in the background.
 */

const CHECK_MS = 3 * 60_000;

// TEMPORARY: diagnosing a report that "Refresh" still needs a second click
// in production. Remove this whole block (and the log(...) calls below)
// once that's confirmed fixed - it exists to see exactly which step of the
// service-worker handoff stalls, which isn't reproducible locally.
const log = (...args: unknown[]) => {
  // eslint-disable-next-line no-console
  console.info("[BuildStamp]", ...args);
};

const waitForControllerChange = (timeoutMs = 10_000) =>
  new Promise<boolean>((resolve) => {
    const onChange = () => {
      clearTimeout(timer);
      resolve(true);
    };
    const timer = setTimeout(() => {
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve(false);
    }, timeoutMs);
    navigator.serviceWorker.addEventListener("controllerchange", onChange, { once: true });
  });

// Workbox precaches the whole app on install (fonts, icons, every JS chunk -
// several MB), so this genuinely takes a while on anything but a fast
// connection. Bounded generously rather than left open-ended: a slow install
// still finishes in the background even if this particular click times out.
const waitForInstalled = (worker: ServiceWorker, timeoutMs = 45_000) =>
  new Promise<boolean>((resolve) => {
    if (worker.state === "installed" || worker.state === "activated") {
      resolve(true);
      return;
    }
    const onChange = () => {
      log("installing worker state ->", worker.state);
      if (worker.state === "installed" || worker.state === "activated") {
        clearTimeout(timer);
        worker.removeEventListener("statechange", onChange);
        resolve(true);
      }
    };
    const timer = setTimeout(() => {
      worker.removeEventListener("statechange", onChange);
      resolve(false);
    }, timeoutMs);
    worker.addEventListener("statechange", onChange);
  });

const refreshNow = async () => {
  try {
    if (!("serviceWorker" in navigator)) {
      log("no serviceWorker support - plain reload");
      window.location.reload();
      return;
    }
    const reg = await navigator.serviceWorker.getRegistration();
    log("registration:", reg ? {
      scope: reg.scope,
      active: reg.active?.scriptURL,
      waiting: reg.waiting?.scriptURL,
      installing: reg.installing?.scriptURL,
    } : null);
    if (!reg) {
      window.location.reload();
      return;
    }

    let waiting = reg.waiting;
    if (!waiting) {
      // Nothing parked yet - check now and wait for the install to actually
      // finish (a fresh install with no custom skipWaiting parks in
      // .waiting once "installed", same as one found earlier).
      log("no waiting worker - calling reg.update()");
      await reg.update().catch((e) => log("reg.update() failed:", e));
      log("after update() - installing:", reg.installing?.scriptURL, "waiting:", reg.waiting?.scriptURL);
      if (reg.installing) {
        const installed = await waitForInstalled(reg.installing);
        log(installed ? "install finished" : "TIMED OUT waiting for install (slow connection?) - reloading anyway");
      }
      waiting = reg.waiting;
    }

    if (waiting) {
      log("posting SKIP_WAITING to", waiting.scriptURL);
      const controllerChanged = waitForControllerChange();
      waiting.postMessage({ type: "SKIP_WAITING" });
      const changed = await controllerChanged;
      log(changed ? "controllerchange fired" : "TIMED OUT waiting for controllerchange");
    } else {
      log("still no waiting worker after update() - nothing to activate");
    }
  } catch (e) {
    log("refreshNow() threw:", e);
    // offline, blocked, or no service worker - a plain reload is the
    // honest fallback rather than leaving the button looking stuck
  }
  log("reloading");
  window.location.reload();
};

export const BuildStamp = () => {
  const [serverBuild, setServerBuild] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    log("mounted - this tab:", __BUILD_ID__);
    if ("serviceWorker" in navigator) {
      void navigator.serviceWorker.getRegistration().then((reg) => {
        log("mount-time registration:", reg ? {
          scope: reg.scope,
          active: reg.active?.scriptURL,
          waiting: reg.waiting?.scriptURL,
          installing: reg.installing?.scriptURL,
        } : null);
      });
    }
  }, []);

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.txt?_=${Date.now()}`, {
          cache: "no-store",
        });
        if (!res.ok) return; // dev server or older deploy without the file
        const text = (await res.text()).trim();
        log("version.txt ->", text, "| this tab:", __BUILD_ID__);
        if (!stopped && text && /^[\w.-]{3,40}$/.test(text)) {
          setServerBuild(text);
        }
      } catch {
        // offline or blocked - try again next interval
      }
    };
    void check();
    const interval = setInterval(() => void check(), CHECK_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const outdated =
    serverBuild !== null &&
    serverBuild !== __BUILD_ID__ &&
    !__BUILD_ID__.startsWith("dev-");

  return (
    <>
      <div
        aria-hidden
        title="Deployed version"
        className="fixed bottom-1 right-2 z-40 text-[9px] font-mono text-muted-foreground/50 select-all pointer-events-auto"
      >
        {__BUILD_ID__}
      </div>
      {outdated && !dismissed && (
        <div
          role="status"
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-2.5 shadow-lg text-sm"
        >
          <span>
            A new version is live ({serverBuild}) - this tab is on {__BUILD_ID__}.
          </span>
          <button
            type="button"
            disabled={refreshing}
            className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-60"
            onClick={() => {
              setRefreshing(true);
              void refreshNow();
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
          <button
            type="button"
            className="text-xs text-muted-foreground underline underline-offset-2"
            onClick={() => setDismissed(true)}
          >
            Later
          </button>
        </div>
      )}
    </>
  );
};
